import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    MotionConfig,
    motion,
    useReducedMotion,
} from "motion/react";
import PricingFeatureIllustration from "./PricingFeatureIllustrations";

const BrandMark = () => (
    <svg viewBox="0 0 512 512" aria-hidden="true" focusable="false">
        <path
            fill="currentColor"
            d="M448 256a192 192 0 1 0-384 0 192 192 0 1 0 384 0zM0 256a256 256 0 1 1 512 0 256 256 0 1 1-512 0zm256 80a80 80 0 1 0 0-160 80 80 0 1 0 0 160zm0-224a144 144 0 1 1 0 288 144 144 0 1 1 0-288zm-32 144a32 32 0 1 1 64 0 32 32 0 1 1-64 0z"
        />
    </svg>
);

const ArrowIcon = () => (
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        <path d="M4 10h11M11 6l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CheckIcon = () => (
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        <path d="M5 10.5l3.2 3.2L15.5 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ProductIcon = ({ name }) => {
    const common = {
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: "1.8",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        "aria-hidden": true,
        focusable: "false",
    };

    switch (name) {
        case "overview":
            return <svg {...common}><path d="m4 10 8-6 8 6v9H4z" /><path d="M9 19v-6h6v6" /></svg>;
        case "tables":
            return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="3" /><circle cx="7" cy="9" r="1" /><circle cx="17" cy="9" r="1" /><circle cx="7" cy="15" r="1" /><circle cx="17" cy="15" r="1" /></svg>;
        case "matches":
            return <svg {...common}><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="2.5" /><path d="M12 4v2M20 12h-2M12 20v-2M4 12h2" /></svg>;
        case "players":
            return <svg {...common}><circle cx="9" cy="8" r="3" /><path d="M4 19a5 5 0 0 1 10 0M17 8a2.5 2.5 0 0 1 0 5M17 16a4 4 0 0 1 3 3" /></svg>;
        case "stats":
            return <svg {...common}><path d="M5 20V9M12 20V4M19 20v-7" /><path d="M3 20h18" /></svg>;
        case "queues":
            return <svg {...common}><circle cx="7" cy="7" r="2.5" /><path d="M3 17a4 4 0 0 1 8 0M14 6h7M14 11h7M14 16h7" /></svg>;
        case "wallet":
            return <svg {...common}><path d="M4 7.5h14a2 2 0 0 1 2 2v8H5a2 2 0 0 1-2-2v-10a2 2 0 0 1 2-2h12" /><path d="M15 11h5v4h-5a2 2 0 1 1 0-4Z" /></svg>;
        case "tv":
            return <svg {...common}><rect x="3" y="6" width="18" height="14" rx="3" /><path d="m8 3 4 3 4-3" /></svg>;
        case "scoreboard":
            return <svg {...common}><rect x="3" y="4" width="18" height="16" rx="3" /><path d="M8 9h2v5H8zM14 9h2v5h-2zM12 9v5" /></svg>;
        case "link":
            return <svg {...common}><path d="M10 13a5 5 0 0 0 7.5.5l2-2a5 5 0 0 0-7-7l-1.1 1.1" /><path d="M14 11a5 5 0 0 0-7.5-.5l-2 2a5 5 0 0 0 7 7l1.1-1.1" /></svg>;
        default:
            return <svg {...common}><path d="m5 12 4 4L19 6" /></svg>;
    }
};

const heroEase = [0.22, 1, 0.36, 1];

const AnimatedHero = () => {
    const reduceMotion = useReducedMotion();

    const revealItem = {
        hidden: { opacity: 0, y: reduceMotion ? 0 : 24 },
        visible: { opacity: 1, y: 0, transition: { duration: reduceMotion ? 0.15 : 0.68, ease: heroEase } },
    };

    const revealSequence = {
        hidden: {},
        visible: {
            transition: {
                delayChildren: reduceMotion ? 0 : 0.12,
                staggerChildren: reduceMotion ? 0 : 0.1,
            },
        },
    };

    return (
        <MotionConfig reducedMotion="user">
            <section className="home-hero home-hero-live" aria-labelledby="home-hero-title">
                <div className="home-container home-hero-stage">
                    <motion.div
                        className="home-hero-copy"
                        variants={revealSequence}
                        initial="hidden"
                        animate="visible"
                    >
                        <motion.p className="home-eyebrow" variants={revealItem}>
                            <i /> One system for the whole club
                        </motion.p>
                        <motion.h1 id="home-hero-title" variants={revealItem}>
                            Every table.<br />Every game.<br /><span>One clear view.</span>
                        </motion.h1>
                        <motion.p className="home-hero-lede" variants={revealItem}>
                            Run tables, queues, live scoreboards and connected screens from one place—so
                            your team stays in control and your players stay in the game.
                        </motion.p>
                        <motion.div className="home-hero-actions" variants={revealItem}>
                            <Link to="/login" className="home-button home-button-primary">
                                Open CueClub Manager <ArrowIcon />
                            </Link>
                            <a href="#club-tools" className="home-text-link">
                                Explore the club tools
                            </a>
                        </motion.div>
                    </motion.div>
                </div>

                <div className="home-live-rail" aria-label="CueClub connected tools">
                    <div className="home-container">
                        <span>Tables</span><i />
                        <span>Queues</span><i />
                        <span>Scoreboards</span><i />
                        <span>Performance</span><i />
                        <span>TV Connect</span>
                    </div>
                </div>
            </section>
        </MotionConfig>
    );
};

const ClubSystemVisual = () => (
    <div className="home-stack-visual" role="img" aria-label="A large live scoreboard connected to tables, queues, performance and TV Connect">
        <article className="home-stack-scoreboard">
            <header>
                <span><ProductIcon name="scoreboard" /></span>
                <div><small>Live scoreboard</small><strong>The score everyone can follow.</strong></div>
                <em><i /> Live</em>
            </header>
            <div className="home-stack-score"><span><b>42</b><small>Youssef</small></span><i>FRAME 2</i><span><b>31</b><small>Adam</small></span></div>
        </article>

        <div className="home-stack-grid">
            <article className="is-tables"><span><ProductIcon name="tables" /></span><div><small>Tables</small><strong>See what is ready</strong></div></article>
            <article className="is-queues"><span><ProductIcon name="queues" /></span><div><small>Queues</small><strong>Keep play moving</strong></div></article>
            <article className="is-performance"><span><ProductIcon name="stats" /></span><div><small>Performance</small><strong>Understand activity</strong></div></article>
            <article className="is-tv"><span><ProductIcon name="tv" /></span><div><small>TV Connect</small><strong>Pair tables and TVs</strong></div></article>
        </div>

        <div className="home-stack-status"><i /><span>One shared club state</span><CheckIcon /></div>
    </div>
);

const PublicHome = () => {
    const [menuOpen, setMenuOpen] = useState(false);

    const closeMenu = () => setMenuOpen(false);

    useEffect(() => {
        if (!menuOpen) return undefined;

        const handleEscape = (event) => {
            if (event.key === "Escape") setMenuOpen(false);
        };

        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [menuOpen]);

    return (
        <div className="home-marketing">
            <header className="home-header">
                <div className="home-container home-header-inner">
                    <Link to="/" className="home-brand" aria-label="CueClub Manager home" onClick={closeMenu}>
                        <span className="home-brand-mark"><BrandMark /></span>
                        <span className="home-brand-text">CueClub Manager</span>
                    </Link>

                    <button
                        className="home-menu-button"
                        type="button"
                        aria-label={menuOpen ? "Close navigation" : "Open navigation"}
                        aria-expanded={menuOpen}
                        aria-controls="home-navigation"
                        onClick={() => setMenuOpen((open) => !open)}
                    >
                        <span /><span /><span />
                    </button>

                    <nav id="home-navigation" className={`home-navigation ${menuOpen ? "is-open" : ""}`} aria-label="Main navigation">
                        <a href="#club-tools" onClick={closeMenu}>Club tools</a>
                        <a href="#club-operations" onClick={closeMenu}>Busy nights</a>
                        <a href="#workspace" onClick={closeMenu}>How it connects</a>
                        <Link to="/login" className="home-nav-login" onClick={closeMenu}>Log in</Link>
                    </nav>
                </div>
            </header>

            <main>
                <AnimatedHero />

                <section className="home-signal-strip" aria-label="Supported club experiences">
                    <div className="home-container home-signal-inner">
                        <p>One room. One source of truth.</p>
                        <div aria-label="Supported cue sports">
                            <span>Snooker</span><i />
                            <span>English pool</span><i />
                            <span>US pool</span><i />
                            <span>Chinese pool</span>
                        </div>
                    </div>
                </section>

                <section className="home-tools-section" id="club-tools" aria-labelledby="home-tools-title">
                    <div className="home-container">
                        <div className="home-section-heading home-section-heading-centered">
                            <p className="home-section-kicker">Made for the room</p>
                            <h2 id="home-tools-title">The tools a great club night needs.</h2>
                            <p>No scattered sheets, no mystery queues, no score calls across the room.</p>
                        </div>

                        <div className="home-tools-grid">
                            <article className="home-tool-card home-tool-card-wide is-mint">
                                <div className="home-tool-copy">
                                    <span className="home-tool-label">Live scoreboards</span>
                                    <h3>The score belongs on the big screen.</h3>
                                    <p>Every point reaches the table display as the game is played, keeping players and spectators together.</p>
                                </div>
                                <div className="home-tool-art home-tool-art-score">
                                    <PricingFeatureIllustration type="score" />
                                    <div className="home-tool-score-detail">
                                        <span>FRAME 02</span>
                                        <strong>42 <i>:</i> 31</strong>
                                        <small>LIVE · TABLE 01</small>
                                    </div>
                                </div>
                            </article>

                            <article className="home-tool-card is-lilac">
                                <div className="home-tool-icon"><PricingFeatureIllustration type="queues" /></div>
                                <span className="home-tool-label">Table queues</span>
                                <h3>Everyone knows who is next.</h3>
                                <p>A clear waiting order for staff, players and the room.</p>
                            </article>

                            <article className="home-tool-card is-sand">
                                <div className="home-tool-icon"><PricingFeatureIllustration type="streaming" /></div>
                                <span className="home-tool-label">Live match links</span>
                                <h3>Take the match beyond the room.</h3>
                                <p>Share a live match view or use the configurable overlay in your stream.</p>
                            </article>

                            <article className="home-tool-card home-tool-card-wide is-ink">
                                <div className="home-tool-copy">
                                    <span className="home-tool-label">Club statistics</span>
                                    <h3>Know the pulse of your club.</h3>
                                    <p>See play time, table activity, match history and performance with context—not another spreadsheet.</p>
                                </div>
                                <div className="home-insight-art" aria-hidden="true">
                                    <div className="home-insight-top"><span>Weekly activity</span><b>LIVE DATA</b></div>
                                    <div className="home-insight-number"><strong>184</strong><span>frames played</span></div>
                                    <div className="home-insight-bars">
                                        <i style={{ height: "38%" }} /><i style={{ height: "54%" }} />
                                        <i style={{ height: "46%" }} /><i style={{ height: "70%" }} />
                                        <i style={{ height: "62%" }} /><i style={{ height: "86%" }} />
                                        <i className="is-current" style={{ height: "100%" }} />
                                    </div>
                                    <div className="home-insight-days"><span>M</span><span>T</span><span>W</span><span>T</span><span>F</span><span>S</span><span>S</span></div>
                                </div>
                            </article>
                        </div>
                    </div>
                </section>

                <section className="home-control-section" id="club-operations" aria-labelledby="home-control-title">
                    <div className="home-container home-control-grid">
                        <div className="home-control-copy">
                            <p className="home-section-kicker home-section-kicker-light">Calm at peak time</p>
                            <h2 id="home-control-title">Your busiest night should still feel under control.</h2>
                            <p>
                                CueClub turns the moving parts of the room into one clear operating view,
                                so staff can act before a queue becomes a question.
                            </p>
                            <ul>
                                <li><CheckIcon /> See table availability at a glance</li>
                                <li><CheckIcon /> Move players from waiting to playing</li>
                                <li><CheckIcon /> Keep staff and screens on the same state</li>
                            </ul>
                        </div>

                        <div className="home-night-board" role="img" aria-label="Illustration of a busy club night">
                            <div className="home-night-heading"><div><i /><span>Friday night</span></div><strong>8:00 — 11:00 PM</strong></div>
                            <div className="home-night-statuses">
                                <span><i className="is-live" /> Playing <b>4</b></span>
                                <span><i className="is-waiting" /> Waiting <b>6</b></span>
                                <span><i className="is-open" /> Open <b>2</b></span>
                            </div>
                            <div className="home-night-tables">
                                <article><span>01</span><div><strong>Youssef vs Adam</strong><small>Snooker · Frame 2</small></div><em className="is-live">LIVE</em></article>
                                <article><span>02</span><div><strong>Karim vs Samir</strong><small>English pool · Race to 5</small></div><em className="is-live">LIVE</em></article>
                                <article><span>03</span><div><strong>Next: Yassine</strong><small>2 players in queue</small></div><em className="is-next">NEXT</em></article>
                                <article><span>04</span><div><strong>Ready to play</strong><small>No active game</small></div><em className="is-open">OPEN</em></article>
                            </div>
                            <div className="home-night-footer"><span>All displays connected</span><i><CheckIcon /></i></div>
                        </div>
                    </div>
                </section>

                <section className="home-workspace-section" id="workspace" aria-labelledby="home-workspace-title">
                    <div className="home-container home-workspace-grid">
                        <div className="home-workspace-copy">
                            <p className="home-section-kicker">One connected club system</p>
                            <h2 id="home-workspace-title">Each tool has a job. Together, they run the room.</h2>
                            <p>
                                Tables, queues, matches, performance and TV Connect share the same club state,
                                so every action starts with the right context.
                            </p>
                            <div className="home-workspace-list">
                                <span><b>01</b> A live overview of the club</span>
                                <span><b>02</b> A live scoreboard for every match</span>
                                <span><b>03</b> Queue tools for every enabled table</span>
                                <span><b>04</b> Performance insights and TV Connect</span>
                            </div>
                        </div>
                        <ClubSystemVisual />
                    </div>
                </section>

                <section className="home-final-cta" aria-labelledby="home-final-title">
                    <div className="home-container home-final-inner">
                        <p className="home-section-kicker home-section-kicker-light">Ready when the room is</p>
                        <h2 id="home-final-title">Bring calm to the whole club.</h2>
                        <p>Open CueClub Manager and keep the next game, the next table and every score moving together.</p>
                        <Link to="/login" className="home-button home-button-lime">
                            Open CueClub Manager <ArrowIcon />
                        </Link>
                    </div>
                </section>
            </main>

            <footer className="home-footer">
                <div className="home-container home-footer-inner">
                    <Link to="/" className="home-brand home-brand-footer" aria-label="CueClub Manager home">
                        <span className="home-brand-mark"><BrandMark /></span>
                        <span className="home-brand-text">CueClub Manager</span>
                    </Link>
                    <p>Built to keep cue-sport clubs in play.</p>
                    <nav aria-label="Legal">
                        <Link to="/privacy-policy">Privacy</Link>
                        <Link to="/general-conditions">General conditions</Link>
                        <Link to="/login">Log in</Link>
                    </nav>
                    <small>© {new Date().getFullYear()} CueClub Manager</small>
                </div>
            </footer>
        </div>
    );
};

export default PublicHome;
